import { useMemo, useState } from 'react'
import type {
    BossLegacyImportResult, BossLegacyReport, BossLegacyStoreGroup, BossLegacyStoreShortcuts,
    BossReportSummary, BossShortcutGroup,
} from '../../types/boss'
import { BossBadges } from './ShortcutPanel'
// The picker's filter box behaves exactly like the pre-filter dialog's — one implementation,
// two callers, so a quoted phrase means the same thing everywhere in this feature.
import { matchesFilterQuery } from './PreFilterDialog'

export type ReportPickerTab = 'web' | 'legacy'

/**
 * How the Import from Legacy tab lays the corpus out: every report grouped by the store that
 * created it, or one store's own legacy shortcut bar — the groups, and the order within them, that
 * the store's staff actually worked from.
 */
export type LegacyPickerView = 'all' | 'myStore'

/**
 * The "Pin to…" sentinel for "New group…". A group id is a GUID, so no real option can collide
 * with it, and the empty string stays the select's own "nothing chosen" value.
 */
const NEW_GROUP = '__new__'

export type ReportPickerProps = {
    /** Which tab is showing. Held by the page because the legacy query is enabled off it. */
    tab?: ReportPickerTab
    onTab?: (tab: ReportPickerTab) => void
    /** boss.reports.import. Cosmetic only — /legacy/* is gated on the same key on the server. */
    canImport?: boolean
    /**
     * boss.reports.delete. Cosmetic only — DELETE /reports/{id} is gated on the same key on the
     * server, so hiding the button never decides anything.
     */
    canDelete?: boolean

    reports?: BossReportSummary[]
    reportsLoading?: boolean
    reportsError?: string | null

    /** Which arrangement the legacy tab shows. Held by the page, which enables each query off it. */
    legacyView?: LegacyPickerView
    onLegacyView?: (view: LegacyPickerView) => void

    legacyGroups?: BossLegacyStoreGroup[]
    legacyLoading?: boolean
    legacyError?: string | null

    /** The "My store" view: every store with a legacy shortcut bar, and the bar on show. */
    storeShortcuts?: BossLegacyStoreShortcuts | null
    storeShortcutsLoading?: boolean
    storeShortcutsError?: string | null
    /** The store the user chose; null until they choose, while the server shows their own. */
    shortcutStoreNum?: number | null
    onShortcutStore?: (storeNum: number) => void

    importing?: boolean
    importResult?: BossLegacyImportResult | null

    /**
     * Groups a report can be pinned into. Empty is an ordinary state rather than a dead end:
     * shortcuts are per-user and seeded to nobody (spec §2.2), so EVERY user starts with none.
     * The picker therefore always offers "Pin to…", with "New group…" to name the first one.
     */
    shortcutGroups?: BossShortcutGroup[]
    selectedReportId?: string

    onOpenReport?: (reportId: string) => void
    onPin?: (groupId: string, reportId: string) => void
    /** "New group…": creates the group, then pins the report into it. */
    onPinToNewGroup?: (name: string, reportId: string) => void
    onImport?: (legacyReportGuids: string[], onConflict: 'skip' | 'overwrite') => void
    /** Asks for the confirm dialog; the page owns it, because deleting outlives this modal. */
    onDeleteReport?: (reportId: string, name: string) => void
    onClose?: () => void
}

/** Runs the legacy filter-box rule over everything about a row a user might type. */
function matches(query: string, ...haystack: (string | null | undefined)[]): boolean {
    return matchesFilterQuery(haystack.filter(Boolean).join(' '), query)
}

/** "7 — VPO Nelson (your store)": the number first, as the legacy store chooser listed them. */
function storeOptionLabel(storeNum: number, storeName: string | null, isAssigned: boolean): string {
    return `${storeNum} — ${storeName ?? `Store ${storeNum}`}${isAssigned ? ' (your store)' : ''}`
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/**
 * The "open a report" modal: web reports grouped by the store they came from, and — for someone
 * who may import — the legacy BOSS corpus, either grouped the same way or laid out as one store's
 * legacy shortcut bar, with an already-imported marker.
 *
 * Every prop is optional so the modal can be rendered in isolation by the style/modal tests.
 */
export default function ReportPicker({
    tab = 'web', onTab, canImport = false, canDelete = false,
    reports = [], reportsLoading = false, reportsError = null,
    legacyView = 'all', onLegacyView,
    legacyGroups = [], legacyLoading = false, legacyError = null,
    storeShortcuts = null, storeShortcutsLoading = false, storeShortcutsError = null,
    shortcutStoreNum = null, onShortcutStore,
    importing = false, importResult = null,
    shortcutGroups = [], selectedReportId = '',
    onOpenReport, onPin, onPinToNewGroup, onImport, onDeleteReport, onClose,
}: ReportPickerProps) {
    const [query, setQuery] = useState('')
    // Shared by both legacy views and keyed by legacy guid, so a report ticked in one view stays
    // ticked in the other — and a report a store filed under two groups is one selection, not two.
    const [picked, setPicked] = useState<string[]>([])
    const [replaceExisting, setReplaceExisting] = useState(false)
    // Which row is asking for a new group name, and the name so far. Inline in the row rather
    // than a second modal: the picker is already a dimming dialog, and stacking one over it to
    // collect thirty characters is more ceremony than the ShortcutPanel's own "＋ Group" form.
    const [namingFor, setNamingFor] = useState<string | null>(null)
    const [newGroupName, setNewGroupName] = useState('')

    const cancelNaming = () => { setNamingFor(null); setNewGroupName('') }

    const submitNewGroup = (reportId: string) => {
        const name = newGroupName.trim()
        if (!name) return
        cancelNaming()
        onPinToNewGroup?.(name, reportId)
    }

    // The legacy corpus is only offered to someone who may import it: the endpoint requires
    // boss.reports.import, so showing the tab to anyone else only buys a 403.
    const showLegacy = canImport
    const activeTab: ReportPickerTab = showLegacy ? tab : 'web'

    /**
     * The Web tab, grouped by the STORE a report came from — not by whoever imported it.
     *
     * Report definitions are shared: every user sees every report, and a legacy import stamps
     * `ownerDisplayName` with whichever administrator happened to click Import. Grouping by that
     * name filed the whole corpus under one person and read as "these are someone else's
     * reports", which is the opposite of what they are. The store of origin is the grouping the
     * legacy tool itself used and the one that means something to a reader.
     *
     * `ownerDisplayName` stays searchable — "who brought this in" is still a fair thing to type.
     */
    const byOrigin = useMemo(() => {
        const map = new Map<string, BossReportSummary[]>()
        for (const r of reports) {
            // Deliberately not searched: memo. In this corpus the legacy Memo field was used as a
            // scratchpad for the report's own SQL, so matching it would surface rows for reasons
            // the user cannot see — and the picker no longer shows the memo at all.
            if (!matches(query, r.name, r.ownerDisplayName, r.legacyStoreName)) continue
            const origin = r.legacyStoreName ?? 'Built on the web'
            const bucket = map.get(origin)
            if (bucket) bucket.push(r)
            else map.set(origin, [r])
        }
        return [...map.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([origin, rows]) => ({
                origin,
                rows: rows.slice().sort((a, b) => a.name.localeCompare(b.name)),
            }))
    }, [reports, query])

    const legacyFiltered = useMemo(() => legacyGroups
        .map(g => ({
            ...g,
            reports: g.reports.filter(r => matches(query, r.name, r.linkedReportName, g.storeLabel)),
        }))
        .filter(g => g.reports.length > 0), [legacyGroups, query])

    // The store's bar keeps the server's order untouched — that order IS the layout. An empty
    // group is part of the layout too, so it stays while nothing is typed; once the filter is
    // narrowing, a group with nothing to offer is noise.
    const narrowing = query.trim().length > 0
    const shortcutGroupsFiltered = useMemo(() => (storeShortcuts?.groups ?? [])
        .map(g => ({
            ...g,
            reports: g.reports.filter(r => matches(query, r.name, r.linkedReportName, g.name)),
        }))
        .filter(g => g.reports.length > 0 || !narrowing), [storeShortcuts, query, narrowing])

    // What the store select shows: the user's own choice the moment they make it, so it does not
    // jump back to the previous store while the new bar loads; until then, the server's answer.
    const shownStoreNum = shortcutStoreNum ?? storeShortcuts?.storeNum ?? null
    const shownStore = storeShortcuts?.stores.find(s => s.storeNum === shownStoreNum) ?? null

    const toggle = (guid: string) =>
        setPicked(cur => (cur.includes(guid) ? cur.filter(g => g !== guid) : [...cur, guid]))

    const toggleGroup = (guids: string[]) =>
        setPicked(cur => (guids.every(g => cur.includes(g))
            ? cur.filter(g => !guids.includes(g))
            : [...cur, ...guids.filter(g => !cur.includes(g))]))

    const startImport = () => {
        if (picked.length === 0) return
        onImport?.(picked, replaceExisting ? 'overwrite' : 'skip')
        setPicked([])
    }

    const needsReview = importResult?.results.filter(r => r.needsReview) ?? []

    /** One legacy report: the same row in both views, so ticking and opening work identically. */
    const legacyRow = (r: BossLegacyReport) => (
        <div key={r.legacyReportGuid} className="boss-picker-row">
            <input
                type="checkbox"
                className="boss-picker-check"
                aria-label={`Select ${r.name}`}
                checked={picked.includes(r.legacyReportGuid)}
                onChange={() => toggle(r.legacyReportGuid)}
            />
            <div className="boss-picker-main">
                <div className="boss-picker-name">
                    <span>{r.name}</span>
                    <BossBadges isCore={r.isCore} />
                    {r.alreadyImported && (
                        <span
                            className="boss-badge boss-badge-imported"
                            title="This report has already been imported"
                        >✓ imported</span>
                    )}
                </div>
                {!r.hasOwnSql && (
                    <div className="boss-picker-memo">
                        {`inherits its query from “${r.linkedReportName ?? 'a linked report'}”`}
                    </div>
                )}
            </div>
            {r.importedReportId !== null && (
                <div className="boss-picker-actions">
                    <button
                        type="button"
                        className="btn btn-secondary boss-btn-sm"
                        onClick={() => onOpenReport?.(String(r.importedReportId))}
                    >Open</button>
                </div>
            )}
        </div>
    )

    /** A titled group of legacy rows with its "select all". */
    const legacyGroup = (key: string, title: string, rows: BossLegacyReport[]) => {
        const guids = rows.map(r => r.legacyReportGuid)
        const allPicked = guids.every(id => picked.includes(id))
        return (
            <div key={key} className="boss-picker-group">
                <div className="boss-picker-group-title">
                    <span>{title}</span>
                    {rows.length > 0 && (
                        <button
                            type="button"
                            className="boss-icon-btn"
                            onClick={() => toggleGroup(guids)}
                        >{allPicked ? 'clear' : 'select all'}</button>
                    )}
                </div>
                {rows.length === 0
                    ? <div className="boss-muted boss-picker-group-empty">No reports in this group.</div>
                    : rows.map(legacyRow)}
            </div>
        )
    }

    /** The "My store" view's own states, before and instead of its groups. */
    const storeShortcutsBody = () => {
        if (storeShortcutsLoading) return <div className="boss-muted">Reading the store’s shortcut layout…</div>
        if (storeShortcutsError) return <div className="boss-error">{storeShortcutsError}</div>
        if (!storeShortcuts) return null
        if (storeShortcuts.storeNum === null) {
            return (
                <div className="boss-empty">
                    {storeShortcuts.stores.length > 0
                        ? 'You are not assigned to a store that set up a shortcut bar in the legacy tool. Choose a store above to see how it organised its reports.'
                        : 'No store set up a shortcut bar in the legacy tool.'}
                </div>
            )
        }
        if (storeShortcuts.groups.length === 0) {
            return (
                <div className="boss-empty">
                    {`Store ${storeShortcuts.storeNum} had no shortcut groups in the legacy tool.`}
                </div>
            )
        }
        if (shortcutGroupsFiltered.length === 0) {
            return <div className="boss-empty">No legacy reports match that filter.</div>
        }
        return shortcutGroupsFiltered.map(g => legacyGroup(g.groupGuid, g.name, g.reports))
    }

    // Dimming modal: deliberately no backdrop onClick — dialogs must not close on outside-click.
    return (
        <div className="modal-backdrop">
            <div className="modal boss-picker">
                <div className="modal-header">
                    <h2>Open a report</h2>
                    <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="modal-body">
                    {showLegacy && (
                        <div className="boss-tabs" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'web'}
                                className={`boss-tab${activeTab === 'web' ? ' active' : ''}`}
                                onClick={() => onTab?.('web')}
                            >Web reports</button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'legacy'}
                                className={`boss-tab${activeTab === 'legacy' ? ' active' : ''}`}
                                onClick={() => onTab?.('legacy')}
                            >Import from Legacy</button>
                        </div>
                    )}

                    {activeTab === 'legacy' && (
                        <div className="boss-legacy-views">
                            <div className="boss-segmented" role="group" aria-label="Arrange the legacy reports">
                                <button
                                    type="button"
                                    aria-pressed={legacyView === 'all'}
                                    className={`boss-segment${legacyView === 'all' ? ' active' : ''}`}
                                    title="Every legacy report, grouped by the store that created it"
                                    onClick={() => onLegacyView?.('all')}
                                >All reports</button>
                                <button
                                    type="button"
                                    aria-pressed={legacyView === 'myStore'}
                                    className={`boss-segment${legacyView === 'myStore' ? ' active' : ''}`}
                                    title="The reports laid out as a store's legacy shortcut bar"
                                    onClick={() => onLegacyView?.('myStore')}
                                >My store</button>
                            </div>
                            {legacyView === 'myStore' && (<>
                                <label className="boss-legacy-store">
                                    Store
                                    <select
                                        className="form-select"
                                        value={shownStoreNum === null ? '' : String(shownStoreNum)}
                                        disabled={!storeShortcuts}
                                        onChange={e => {
                                            if (e.target.value) onShortcutStore?.(Number(e.target.value))
                                        }}
                                    >
                                        {shownStoreNum === null && <option value="">Choose a store…</option>}
                                        {(storeShortcuts?.stores ?? []).map(s => (
                                            <option key={s.storeNum} value={String(s.storeNum)}>
                                                {storeOptionLabel(s.storeNum, s.storeName, s.isAssigned)}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {/* Beside the label, not in it: text inside a label becomes part
                                    of the select's accessible name. */}
                                {shownStore && (
                                    <span className="boss-legacy-store-summary">
                                        {`${plural(shownStore.groupCount, 'group', 'groups')} · ${plural(shownStore.reportCount, 'report', 'reports')}`}
                                    </span>
                                )}
                            </>)}
                        </div>
                    )}

                    <input
                        className="form-input boss-filter-box"
                        style={{ marginTop: 'var(--space-3)' }}
                        placeholder={'Filter reports — words in any order, or "an exact phrase"'}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                    />

                    {activeTab === 'web' ? (
                        <div className="boss-picker-list">
                            {reportsLoading && <div className="boss-muted">Loading reports…</div>}
                            {reportsError && <div className="boss-error">{reportsError}</div>}
                            {!reportsLoading && !reportsError && byOrigin.length === 0 && (
                                <div className="boss-empty">No reports match that filter.</div>
                            )}
                            {byOrigin.map(group => (
                                <div key={group.origin} className="boss-picker-group">
                                    <div className="boss-picker-group-title">{group.origin}</div>
                                    {group.rows.map(r => (
                                        <div
                                            key={r.id}
                                            className={`boss-picker-row${selectedReportId === r.id ? ' active' : ''}`}
                                        >
                                            <div className="boss-picker-main">
                                                <div className="boss-picker-name">
                                                    <span>{r.name}</span>
                                                    <BossBadges
                                                        isCore={r.isCore}
                                                        hasPreFilters={r.hasPreFilters}
                                                        hasMyStore={r.hasMyStore}
                                                        needsReview={r.needsReview}
                                                    />
                                                </div>
                                                {(r.linkedReportName || r.legacyStoreName) && (
                                                    <div className="boss-picker-memo">
                                                        {[
                                                            r.linkedReportName ? `inherits its query from “${r.linkedReportName}”` : null,
                                                            r.legacyStoreName ? `imported from ${r.legacyStoreName}` : null,
                                                        ].filter(Boolean).join(' · ')}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="boss-picker-actions">
                                                {namingFor === r.id ? (
                                                    <div className="boss-pin-new">
                                                        <input
                                                            className="form-input"
                                                            autoFocus
                                                            maxLength={30}
                                                            placeholder="New group name"
                                                            aria-label={`Name the group to pin ${r.name} into`}
                                                            value={newGroupName}
                                                            onChange={e => setNewGroupName(e.target.value)}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter') submitNewGroup(r.id)
                                                                if (e.key === 'Escape') cancelNaming()
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary boss-btn-sm"
                                                            disabled={!newGroupName.trim()}
                                                            onClick={() => submitNewGroup(r.id)}
                                                        >Pin</button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary boss-btn-sm"
                                                            onClick={cancelNaming}
                                                        >Cancel</button>
                                                    </div>
                                                ) : (
                                                    <select
                                                        className="form-select boss-pin-select"
                                                        value=""
                                                        aria-label={`Pin ${r.name} to a shortcut group`}
                                                        onChange={e => {
                                                            const value = e.target.value
                                                            if (!value) return
                                                            if (value === NEW_GROUP) {
                                                                setNewGroupName('')
                                                                setNamingFor(r.id)
                                                            } else {
                                                                onPin?.(value, r.id)
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Pin to…</option>
                                                        {shortcutGroups.map(g => (
                                                            <option key={g.id} value={g.id}>{g.name}</option>
                                                        ))}
                                                        <option value={NEW_GROUP}>New group…</option>
                                                    </select>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger boss-btn-sm"
                                                        title={`Permanently delete ${r.name}`}
                                                        onClick={() => onDeleteReport?.(r.id, r.name)}
                                                    >Delete</button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="btn btn-primary boss-btn-sm"
                                                    onClick={() => onOpenReport?.(r.id)}
                                                >Open</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="boss-picker-list">
                            {legacyView === 'myStore' ? storeShortcutsBody() : (
                                <>
                                    {legacyLoading && <div className="boss-muted">Reading the legacy report list…</div>}
                                    {legacyError && <div className="boss-error">{legacyError}</div>}
                                    {!legacyLoading && !legacyError && legacyFiltered.length === 0 && (
                                        <div className="boss-empty">No legacy reports match that filter.</div>
                                    )}
                                    {legacyFiltered.map(g => legacyGroup(g.storeLabel, g.storeLabel, g.reports))}
                                </>
                            )}

                            {importResult && (
                                <div className="boss-import-result">
                                    <div>
                                        Imported {importResult.imported}, skipped {importResult.skipped},
                                        failed {importResult.failed}.
                                    </div>
                                    {needsReview.length > 0 && (
                                        <div className="boss-import-review">
                                            These imported but cannot run until a designer fixes them:
                                            <ul>
                                                {needsReview.map(r => (
                                                    <li key={r.legacyReportGuid}>
                                                        <strong>{r.name}</strong> — {r.needsReviewReason}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <div className="boss-picker-foot">
                        {activeTab === 'legacy' && (
                            <>
                                <label className="boss-check">
                                    <input
                                        type="checkbox"
                                        checked={replaceExisting}
                                        onChange={e => setReplaceExisting(e.target.checked)}
                                    />
                                    Replace definitions that were already imported
                                </label>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={importing || picked.length === 0}
                                    onClick={startImport}
                                >{importing ? 'Importing…' : `Import ${picked.length} selected`}</button>
                            </>
                        )}
                        <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ marginLeft: 'auto' }}
                            onClick={onClose}
                        >Close</button>
                    </div>
                </div>
            </div>
        </div>
    )
}
